import React from 'react';
import { Info, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { Card, CardContent } from '../../ui/Card';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/shadcn';
import { cn } from '../../../utils';
import type { DashboardKpi } from '../../../types';
import { getMetricDef, MetricId } from './metricDefs';
import { metricIcon, metricTone, toneStyle } from './metricTones';

interface HeadlineStatCardProps {
  metric: MetricId;
  kpi?: DashboardKpi;
  invertDelta?: boolean;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value);
}

const DeltaBadge: React.FC<{ deltaPct: number | null; invert: boolean }> = ({
  deltaPct,
  invert,
}) => {
  if (deltaPct === null) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
        N/A
      </span>
    );
  }
  const isFlat = Math.abs(deltaPct) < 0.05;
  const isImprovement = invert ? deltaPct < 0 : deltaPct > 0;
  const Icon = isFlat ? Minus : deltaPct > 0 ? TrendingUp : TrendingDown;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold',
        isFlat
          ? 'bg-muted text-muted-foreground'
          : isImprovement
            ? 'bg-emerald-50 text-emerald-700'
            : 'bg-red-50 text-red-700',
      )}
    >
      <Icon className="h-3 w-3" />
      {deltaPct > 0 ? '+' : ''}
      {deltaPct.toLocaleString('en-US', { maximumFractionDigits: 1 })}%
    </span>
  );
};

/** Prominent headline KPI (platform / shop net profit). */
const HeadlineStatCard: React.FC<HeadlineStatCardProps> = ({ metric, kpi, invertDelta = false }) => {
  const def = getMetricDef(metric);
  const tone = toneStyle(metricTone(metric));
  const Icon = metricIcon(metric);
  const value = kpi?.value ?? 0;
  const isNegative = value < 0;

  return (
    <Card className={cn('border-blue-200', tone.surface)}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="text-sm font-medium text-muted-foreground">{def.label}</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={`What is ${def.label}?`}
                  className="text-blue-400 hover:text-blue-700"
                >
                  <Info className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                {def.description}
              </TooltipContent>
            </Tooltip>
          </div>
          {Icon && (
            <span
              className={cn(
                'inline-flex h-8 w-8 items-center justify-center rounded-lg',
                tone.chipBg,
                tone.chipText,
              )}
            >
              <Icon className="h-4 w-4" />
            </span>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div
            className={cn(
              'text-3xl font-bold tabular-nums tracking-tight',
              isNegative ? 'text-red-700' : tone.value,
            )}
          >
            {kpi != null ? formatCurrency(value) : '—'}
          </div>
          {kpi?.deltaPct !== undefined && (
            <DeltaBadge deltaPct={kpi.deltaPct ?? null} invert={invertDelta} />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default HeadlineStatCard;
